import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { FormDialog } from '@/components/shared/form-dialog';
import { FormField } from '@/components/shared/form-field';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { api, type Collection } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';

interface ChronicleCategory {
  id: string;
  name: string;
  momentCount: number;
}

interface ChronicleCategoriesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ChronicleCategoriesDialog = ({ open, onOpenChange }: ChronicleCategoriesDialogProps) => {
  const { t } = useTranslation('content');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const membership = useCurrentMembership();
  const canEdit = hasPermission(membership, PERMISSIONS.CONTENT_EDIT);
  const canCreate = hasPermission(membership, PERMISSIONS.CONTENT_CREATE);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ChronicleCategory | null>(null);
  const [name, setName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<ChronicleCategory | null>(null);

  const categories = useQuery({
    queryKey: ['content', 'chronicle', 'categories'],
    queryFn: () =>
      api.get<Collection<ChronicleCategory>>('/content/chronicle/categories', {
        query: { page: 1, pageSize: 100 },
      }),
    enabled: open,
  });

  const rows = categories.data?.data ?? [];

  const resetForm = () => {
    setEditing(null);
    setName('');
    setFormOpen(false);
  };

  const openCreate = () => {
    setEditing(null);
    setName('');
    setFormOpen(true);
  };

  const openEdit = (category: ChronicleCategory) => {
    setEditing(category);
    setName(category.name);
    setFormOpen(true);
  };

  const save = useMutation({
    mutationFn: () => {
      const payload = { name: name.trim() };
      if (editing) {
        return api.patch<ChronicleCategory>(`/content/chronicle/categories/${editing.id}`, payload);
      }
      return api.post<ChronicleCategory>('/content/chronicle/categories', payload);
    },
    onSuccess: () => {
      toast.success(editing ? t('chronicle.categories.saved') : t('chronicle.categories.created'));
      resetForm();
      void queryClient.invalidateQueries({ queryKey: ['content', 'chronicle', 'categories'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/content/chronicle/categories/${id}`),
    onSuccess: () => {
      toast.success(t('chronicle.categories.deleted'));
      setDeleteTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['content', 'chronicle', 'categories'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const columns: DataTableColumn<ChronicleCategory>[] = [
    {
      id: 'name',
      header: t('chronicle.categories.columns.name'),
      cell: (row) => row.name,
    },
    {
      id: 'moments',
      header: t('chronicle.categories.columns.moments'),
      cell: (row) => t('chronicle.categories.count', { count: row.momentCount }),
      hideBelowMd: true,
    },
  ];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('chronicle.categories.title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {canCreate ? (
              <div className="flex justify-end">
                <Button type="button" size="sm" onClick={openCreate}>
                  {t('chronicle.categories.add')}
                </Button>
              </div>
            ) : null}
            <DataTable
              rows={rows}
              columns={columns}
              rowKey={(row) => row.id}
              loading={categories.isLoading}
              empty={<p className="text-body-sm text-fg-secondary">{t('chronicle.categories.empty')}</p>}
              rowActions={(row) =>
                canEdit ? (
                  <div className="flex justify-end gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      aria-label={t('chronicle.categories.edit')}
                      onClick={() => openEdit(row)}
                    >
                      <Pencil className="size-4" aria-hidden />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      aria-label={t('chronicle.categories.delete')}
                      disabled={row.momentCount > 0}
                      onClick={() => setDeleteTarget(row)}
                    >
                      <Trash2 className="size-4" aria-hidden />
                    </Button>
                  </div>
                ) : null
              }
              actionsHeader={tCommon('table.actions')}
            />
            <p className="text-body-sm text-fg-secondary">{t('chronicle.categories.hint')}</p>
          </div>
        </DialogContent>
      </Dialog>

      <FormDialog
        open={formOpen}
        onOpenChange={(next) => {
          if (!next) resetForm();
          else setFormOpen(true);
        }}
        title={editing ? t('chronicle.categories.editTitle') : t('chronicle.categories.addTitle')}
        size="sm"
        submitLabel={editing ? t('chronicle.categories.save') : t('chronicle.categories.add')}
        cancelLabel={tCommon('action.cancel')}
        submitting={save.isPending}
        submitDisabled={name.trim().length === 0}
        onSubmit={() => save.mutate()}
      >
        <FormField label={t('chronicle.categories.name')} required>
          {(field) => (
            <Input
              id={field.id}
              value={name}
              onChange={(event) => setName(event.target.value)}
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>
      </FormDialog>

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(next) => {
          if (!next) setDeleteTarget(null);
        }}
        title={t('chronicle.categories.deleteTitle', { name: deleteTarget?.name ?? '' })}
        description={t('chronicle.categories.deleteBody')}
        confirmLabel={t('chronicle.categories.delete')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={remove.isPending}
        onConfirm={() => {
          if (deleteTarget) remove.mutate(deleteTarget.id);
        }}
      />
    </>
  );
};
